import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useCreateCategory, useUpdateCategory } from '@/hooks/useCategories';
import { categorySchema } from "../types";
import type { Category, CategoryFormData, CategoryFormValues } from "../types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCategories } from '@/hooks/useCategories';
import { useMemo } from 'react';
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

interface CategoryFormProps {
  providerId: number;
  versionId: number;
  parentId?: number;
  initialData?: CategoryFormValues;
  onSuccess?: () => void;
  onSubmit?: (data: CategoryFormData) => Promise<void>;
  isSubmitting?: boolean;
  onCancel?: () => void;
}

interface CategoryOption {
  label: React.ReactNode;
  value: string;
  parent_id: number | null;
}

interface ParentOption {
  label: React.ReactNode;
  value: string;
}

export function CategoryForm({
  providerId,
  versionId,
  initialData,
  onSuccess,
  onSubmit: externalSubmit,
  isSubmitting: externalIsSubmitting,
  onCancel
}: CategoryFormProps) {
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const { data: categories } = useCategories(providerId, versionId);
  
  const form = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: initialData?.name || '',
      parentId: initialData?.parentId || null,
      price: initialData?.price || null,
      description: initialData?.description || '',
      isAddon: initialData?.isAddon || null
    }
  });

  const isSubmitting = externalIsSubmitting ?? form.formState.isSubmitting;

  async function onSubmit(data: CategoryFormData) {
    const formattedData = {
      ...data,
      parentId: data.parentId ?? null,
      displayOrder: data.displayOrder ?? 0
    };

    if (externalSubmit) {
      await externalSubmit(formattedData);
      onSuccess?.();
      return;
    }

    try {
      if (initialData?.id) {
        await updateCategory.mutateAsync({
          id: initialData.id,
          ...formattedData,
          providerId,
          versionId
        });
      } else {
        await createCategory.mutateAsync({
          ...formattedData,
          providerId,
          versionId
        });
      }
      onSuccess?.();
    } catch (error) {
      // Error is handled by the mutation
    }
  }

  // Modified parentOptions to show hierarchy and handle parent/sub categories
  const parentOptions = useMemo(() => {
    if (!categories) return [];
    
    const getOptionLabel = (category: Category, level = 0): CategoryOption => {
      const prefix = level > 0 ? '└─ '.padStart(level * 2 + 2, ' ') : '';
      const isParent = !category.parent_id;
      return {
        label: (
          <div className="flex items-center gap-2">
            <span>{prefix}{category.name}</span>
            <Badge variant={isParent ? "secondary" : "outline"} className="h-5">
              {isParent ? "P" : "S"}
            </Badge>
          </div>
        ),
        value: category.id.toString(),
        parent_id: category.parent_id
      };
    };

    // Only show parent categories as options for parent selection
    const availableCategories = categories.filter((cat: Category) => 
      !cat.parent_id && cat.id !== initialData?.id
    );

    return availableCategories.map((cat: Category) => getOptionLabel(cat));
  }, [categories, initialData?.id]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input {...field} disabled={isSubmitting} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="price"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Price (Optional)</FormLabel>
              <FormControl>
                <Input 
                  type="number"
                  step="0.01"
                  placeholder="Enter price (optional)"
                  {...field}
                  value={field.value === null || field.value === undefined ? '' : field.value}
                  onChange={e => {
                    const val = e.target.value;
                    field.onChange(val === '' ? null : e.target.valueAsNumber);
                  }}
                  disabled={isSubmitting}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea 
                  {...field}
                  value={field.value ?? ''}
                  disabled={isSubmitting}
                  placeholder="Enter category description"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="isAddon"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value === true}
                  onCheckedChange={(checked) => {
                    field.onChange(checked === true ? true : null);
                  }}
                  disabled={isSubmitting}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>
                  Is Add-on
                </FormLabel>
                <p className="text-sm text-muted-foreground">
                  Mark this category as an add-on service
                </p>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="parentId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Parent Category</FormLabel>
              <Select
                value={field.value?.toString() || undefined}
                onValueChange={(value) => {
                  field.onChange(value === "none" ? null : parseInt(value));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select parent category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <div className="flex items-center gap-2">
                      <span>None</span>
                      <Badge variant="secondary" className="h-5">P</Badge>
                    </div>
                  </SelectItem>
                  {parentOptions.map(({ label, value }: ParentOption) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {initialData ? "Update" : "Create"} Category
          </Button>
        </div>
      </form>
    </Form>
  );
}
